import { cva } from 'class-variance-authority';
import { DynamicIcon } from 'phosphor-icons/react';
import { forwardRef } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2Props } from './Button.v2.types';

export const buttonAnimation = `relative overflow-hidden
  before:content-[''] before:absolute before:inset-y-0 before:left-0 before:h-full before:w-0 before:z-0 before:pointer-events-none before:transition-[width] before:duration-300 before:ease-linear
  hover:before:w-full
  before:rounded-inherit
  motion-reduce:before:transition-none`;

export const buttonVariants = cva(
  `[&_svg]:mx-0.5
  inline-flex items-center justify-center whitespace-nowrap select-none ring-offset-background transition-colors
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
  disabled:pointer-events-none`,
  {
    variants: {
      variant: {
        primary: `
          font-bold text-white uppercase bg-blue border-2 border-blue
          hover:bg-blue-hover hover:text-grey-100
          active:bg-blue-hover active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-white active:text-grey-100
          focus:bg-blue focus:ring-2 focus:ring-blue focus:ring-offset-1 focus:ring-offset-white focus:text-grey-100
          disabled:bg-grey-400 disabled:border-grey-400`,
        'primary-red-monochrome': `
          font-bold text-white uppercase bg-red-600
          hover:bg-red-500
          active:bg-red-500 active:ring-2 active:ring-red-500 active:ring-offset-1 active:ring-offset-white
          focus:bg-red-600 focus:ring-2 focus:ring-red-600 focus:ring-offset-1 focus:ring-offset-white
          disabled:bg-grey-400`,
        secondary: `
          bg-transparent font-bold text-grey-100 uppercase border-2 border-blue
          hover:bg-blue-hover hover:text-white
          active:bg-transparent active:border-blue active:ring-2 active:ring-blue active:ring-offset-2 active:ring-offset-black
          focus-visible:bg-transparent focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-black
          disabled:border-grey-400 disabled:text-grey-400 disabled:bg-transparent
          ${buttonAnimation} before:bg-blue-hover 
        `,
        'secondary-blue-monochrome': `
          font-bold uppercase bg-white text-blue border-2 border-blue
          hover:bg-blue-hover hover:text-white
          active:text-white active:bg-blue-hover active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-white
          focus:bg-white focus:text-blue focus:ring-2 focus:ring-offset-1 focus:ring-offset-white focus:ring-blue
          disabled:border-[#999] disabled:text-[#999] disabled:bg-white
          ${buttonAnimation} before:bg-blue-hover
        `,
        'secondary-red-monochrome': `
          font-bold uppercase bg-white text-red-600 border-2 border-red-600
          hover:bg-red-500 hover:text-white
          active:text-white active:bg-red-500 active:ring-2 active:ring-red-500 active:ring-offset-1 active:ring-offset-white
          focus:bg-white focus:text-red-600 focus:ring-2 focus:ring-offset-1 focus:ring-offset-white focus:ring-red-600
          disabled:border-[#999] disabled:text-[#999] disabled:bg-white
          ${buttonAnimation} before:bg-red-500
        `,
        'secondary-black-monochrome': `
          font-bold uppercase bg-white text-grey-800 border border-grey-800 border-2
          hover:bg-neutral-900 hover:bg-grey-800 hover:text-white
          active:text-white active:bg-grey-800 active:ring-2 active:ring-grey-800 active:ring-offset-1 active:ring-offset-white
          focus:bg-white focus:ring-2 focus:ring-offset-1 focus:ring-offset-white focus:ring-grey-800
          disabled:border-0 disabled:text-[#999] disabled:bg-white
          ${buttonAnimation} before:bg-grey-800
        `,
        tertiary: `
          bg-transparent text-white font-bold uppercase border-2 border-transparent
          hover:bg-white hover:text-black
          active:text-black active:bg-white active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-black
          focus:text-white focus:bg-transparent focus:border-blue focus:border-2
          disabled:text-[#999] disabled:bg-transparent`,
        'tertiary-black-monochrome': `
          bg-transparent text-grey-800 font-bold uppercase border-2 bg-transparent border-transparent
          hover:bg-grey-800 hover:text-white
          active:text-white active:bg-grey-800 active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-white
          focus:text-grey-800 focus:bg-transparent focus:border-2 focus:border-blue
          disabled:text-[#999] disabled:bg-transparent`,
        'tertiary-blue-monochrome': `
          bg-transparent text-blue font-medium border-2 border-transparent
          hover:bg-grey-100 focus:outline-offset-2
          active:bg-grey-100
          focus:border-blue focus:border-2 focus:outline-offset-2
          disabled:text-grey-400 disabled:bg-transparent`,
        'tertiary-red-monochrome': `
          bg-transparent text-red-600 font-medium border-2 border-transparent
          hover:bg-grey-100
          active:bg-grey-100
          focus:border-red-600 focus:border-2 focus:outline-offset-2
          disabled:text-grey-400 disabled:bg-transparent`,
        'tertiary-grey-monochrome': `
          bg-transparent text-grey-500 font-medium border-2 border-transparent
          hover:bg-grey-100
          active:bg-grey-100
          focus:border-grey-500 focus:border-2 focus:outline-offset-2
          disabled:text-grey-400 disabled:bg-transparent`,
        'dark-secondary': `
          bg-transparent text-white border-2 border-blue font-bold uppercase
          hover:bg-white hover:text-grey-900 hover:border-2 hover:border-blue
          active:text-white active:ring-2 active:ring-blue active:ring-offset-1 active:ring-offset-black
          focus:text-white focus:bg-transparent focus:border-blue focus:border-2
          disabled:bg-transparent disabled:text-grey-700 disabled:border-grey-700
          `,
        'dark-tertiary': `
          bg-transparent text-white font-bold uppercase
          border-2 border-transparent
          hover:bg-grey-700
          active:bg-grey-700 active:ring-2 active:ring-blue active:ring-offset-2 active:ring-offset-grey-900
          focus:bg-transparent focus:ring-2 focus:ring-blue focus:ring-offset-2 focus:ring-offset-grey-900
          focus-visible:outline-none focus-visible:border-0
          disabled:text-grey-700 disabled:bg-transparent
        `,
      },
      size: {
        large: 'py-3 px-[22px] text-base tracking-[2.88px]',
        medium: 'py-2.5 px-3.5 text-sm tracking-[2.88px]',
        small: 'py-1.5 px-3 text-sm tracking-[2.88px]',
        xsmall: 'py-1.5 px-1.5 text-[13px] tracking-[-0.039px]',
      },
      shape: {
        rounded: 'rounded-full',
        square: 'rounded-lg',
      },
      iconOnly: {
        true: '',
        false: '',
      },
      hasLeftIcon: {
        true: '',
        false: '',
      },
      hasRightIcon: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'large',
      shape: 'rounded',
      iconOnly: false,
    },
    compoundVariants: [
      // Icon only variants for different sizes
      {
        size: 'large',
        iconOnly: true,
        className: 'rounded-full h-12 w-12',
      },
      {
        size: 'medium',
        iconOnly: true,
        className: 'rounded-full h-10 w-10',
      },
      {
        size: 'small',
        iconOnly: true,
        className: 'rounded-full h-9 w-9',
      },
      {
        size: 'xsmall',
        iconOnly: true,
        className: 'rounded-full h-8 w-8',
      },
      // Small size with rounded shape padding
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        className: 'px-[22px]',
      },
      // padding overrides text & icon (left or right)
      {
        size: 'large',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-3',
      },
      {
        size: 'large',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-3',
      },
      {
        size: 'medium',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-2',
      },
      {
        size: 'medium',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-2',
      },
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-3',
      },
      {
        size: 'small',
        shape: 'rounded',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-3',
      },
      {
        size: 'small',
        shape: 'square',
        iconOnly: false,
        hasLeftIcon: true,
        className: 'pl-2 pr-3.5',
      },
      {
        size: 'small',
        shape: 'square',
        iconOnly: false,
        hasRightIcon: true,
        className: 'pr-2 pr-3.5',
      },
    ],
  }
);

export const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      className,
      variant = 'primary',
      size = 'large',
      shape = 'rounded',
      label = '',
      iconLeft,
      iconRight,
      iconClassName,
      iconWeight = 'regular',
      iconSize = 16,
      disabled,
      ...props
    },
    ref
  ) => {
    const isIconOnly = !label || label.trim().length === 0;

    const hasLeftIcon =
      typeof iconLeft === 'string'
        ? iconLeft.trim().length > 0
        : Boolean(iconLeft);
    const hasRightIcon =
      typeof iconRight === 'string'
        ? iconRight.trim().length > 0
        : Boolean(iconRight);

    const hasAnyIcon = hasLeftIcon || hasRightIcon;
    const textGapClass = !isIconOnly && hasAnyIcon ? 'gap-x-0.5' : '';

    return (
      <button
        ref={ref as any}
        type="button"
        className={c(
          buttonVariants({
            variant,
            size,
            shape,
            iconOnly: isIconOnly,
            hasLeftIcon,
            hasRightIcon,
          }),
          textGapClass,
          className
        )}
        disabled={disabled}
        {...props}
      >
        <span className={c('relative inline-flex items-center', textGapClass)}>
          {hasLeftIcon ? (
            <DynamicIcon
              iconName={iconLeft as string}
              className={c(iconClassName)}
              weight={iconWeight}
              size={iconSize}
            />
          ) : null}

          {label}

          {hasRightIcon ? (
            <DynamicIcon
              iconName={iconRight as string}
              className={c(iconClassName)}
              weight={iconWeight}
              size={iconSize}
            />
          ) : null}
        </span>
      </button>
    );
  }
);

ButtonV2.displayName = 'ButtonV2';
